import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * The **support** module: the single owner of the storefront's WhatsApp
 * contact/help link (KAN-13) - the curated page vocabulary a salon's outgoing
 * message can reference, the greeting/message composition, and the `wa.me`
 * URL builder. Raw pathnames carry order/variant ids that are noise to a
 * salon and unsettling to see in their own outgoing message, so every
 * consumer speaks in the curated `key`/`label` pair this module derives
 * rather than the pathname itself.
 */

/** SalonStocker's WhatsApp support number (no leading `+` - `wa.me` format). */
export const SUPPORT_WHATSAPP_NUMBER = "61432894983";

/**
 * SalonStocker's support email address.
 *
 * Lives here beside the WhatsApp number so there is one definition of "how a
 * salon reaches us" rather than a literal per screen. Compose `mailto:` links
 * from it; do not inline the address.
 */
export const SUPPORT_EMAIL = "caroline@salonstocker.com";

export interface SupportPage {
  /**
   * Stable, non-PII slug for analytics. Always one of a fixed set - built only
   * from the route tree below (e.g. `"cart"`, `"account_addresses"`), never
   * from a slug or id in the pathname - or `"other"` / `"home"`.
   */
  key: string;
  /** Human-readable page name for the outgoing message; `null` when unmapped. */
  label: string | null;
}

interface RouteNode {
  /** Human name for this segment in the outgoing message. */
  label: string;
  /** The segments that may follow this one; absent when none is named. */
  children?: ReadonlyMap<string, RouteNode>;
}

/**
 * The pages worth naming in a salon's outgoing message, as a tree that mirrors
 * the real routes. Only a segment in its place in the tree counts, so a product
 * slug that happens to be `stock` is not the account page. Add a node when a new
 * page appears; an unlisted page falls back to its nearest named parent.
 */
/** Named once: it is both a top-level page and a child of Account. */
const SUPPLIER_MATCH = "Supplier Match";

const SUPPORT_ROUTES: ReadonlyMap<string, RouteNode> = new Map([
  ["cart", { label: "Cart" }],
  ["checkout", { label: "Checkout" }],
  ["order-confirmation", { label: "Order Confirmation" }],
  ["orders", { label: "Orders" }],
  ["search", { label: "Search" }],
  ["deals", { label: "Deals" }],
  ["shopping", { label: "Shopping" }],
  ["login", { label: "Sign In" }],
  ["logout", { label: "Log Out" }],
  ["reset-password", { label: "Reset Password" }],
  // The concierge landing page. A salon reaching out from here has a link that
  // will not open, so naming it in their message tells us exactly that.
  ["enter", { label: "Your Cart Link" }],
  // The two first-login steps (KAN-293), which sit outside the app shell. The
  // segments are ACTIVATE_PATH and ONBOARDING_PATH without the leading slash.
  ["activate", { label: "Account Activation" }],
  ["onboarding", { label: "Delivery Address Setup" }],
  [
    "supplier-match",
    {
      label: SUPPLIER_MATCH,
      children: new Map([["confirm", { label: "Confirm" }]])
    }
  ],
  [
    "account",
    {
      label: "Account",
      children: new Map([
        ["addresses", { label: "Addresses" }],
        ["saved-lists", { label: "Saved Lists" }],
        ["stock", { label: "My Stock" }],
        ["messages", { label: "Messages" }],
        ["security", { label: "Security" }],
        ["supplier-match", { label: SUPPLIER_MATCH }],
        ["preferences", { label: "Preferences" }]
      ])
    }
  ]
]);

/**
 * Strips trailing slashes so `/cart/` and `/cart` resolve identically. Exported
 * for the onboarding gate, which matches pathnames the same way.
 */
export function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * The page a pathname belongs to, named from the leading run of known segments.
 * The root path is "Home"; an unknown first segment resolves to
 * `{ key: "other", label: null }`, so the raw path never reaches the message.
 */
export function resolveSupportPage(pathname: string): SupportPage {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  if (segments.length === 0) {
    return { key: "home", label: "Home" };
  }

  const keys: string[] = [];
  const labels: string[] = [];
  let cursor: ReadonlyMap<string, RouteNode> | undefined = SUPPORT_ROUTES;
  for (const segment of segments) {
    const node: RouteNode | undefined = cursor?.get(segment);
    if (!node) break;
    keys.push(segment.replace(/-/g, "_"));
    labels.push(node.label);
    cursor = node.children;
  }

  if (keys.length === 0) {
    return { key: "other", label: null };
  }

  return { key: keys.join("_"), label: labels.join(" / ") };
}

type SupportProfile = Pick<
  SalonStockerHttpTypes.StoreProfile,
  "company_name" | "first_name"
>;

/**
 * The greeting line: the salon's company name, else their first name, else no
 * name at all - never a literal "null" in the outgoing message. `profile` is
 * `null` on the login page, where no one is signed in yet.
 */
function greet(profile: SupportProfile | null | undefined): string {
  const name = profile?.company_name?.trim() || profile?.first_name?.trim();
  return name
    ? `Hi, it's ${name} - I need help with SalonStocker.`
    : "Hi, I need help with SalonStocker.";
}

/**
 * The prefilled WhatsApp message: a name-aware greeting, plus which page the
 * salon reached out from when that page is curated (see
 * {@link resolveSupportPage}).
 */
export function buildSupportMessage(
  profile: SupportProfile | null | undefined,
  pathname: string
): string {
  const { label } = resolveSupportPage(pathname);
  const greeting = greet(profile);
  return label ? `${greeting} (I'm on the ${label} page.)` : greeting;
}

/**
 * The `wa.me` deep link that opens a chat with SalonStocker support,
 * prefilled with {@link buildSupportMessage}.
 */
export function buildSupportWhatsAppUrl(
  profile: SupportProfile | null | undefined,
  pathname: string
): string {
  const message = buildSupportMessage(profile, pathname);
  return `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}
